import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { getConsent, setConsent, onConsentOpen } from "@/lib/consent";

/**
 * Cookie consent banner for the public site. Shows until the visitor makes a
 * choice; non-essential trackers (the Meta Pixel) stay off until "Accept". A
 * footer "Cookie settings" link (openConsentSettings) reopens it to change or
 * withdraw consent. Client-only — renders nothing during SSR/first paint to
 * avoid a hydration mismatch.
 */
const CookieConsent = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(!getConsent().decided);
    return onConsentOpen(() => setOpen(true));
  }, []);

  if (!open) return null;

  const choose = (analytics: boolean) => {
    setConsent(analytics);
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-label={t("cookieConsent.aria", "Cookie consent")}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
    >
      <div className="container mx-auto px-4 py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
        <p className="text-sm text-muted-foreground flex-1">
          {t(
            "cookieConsent.message",
            "We use strictly necessary cookies to run the site, and — only with your consent — analytics and advertising cookies to understand usage and measure ads.",
          )}{" "}
          <Link to="/privacy" className="underline hover:text-foreground">
            {t("cookieConsent.learnMore", "How we use cookies")}
          </Link>
          .
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => choose(false)}>
            {t("cookieConsent.decline", "Decline")}
          </Button>
          <Button size="sm" onClick={() => choose(true)}>
            {t("cookieConsent.accept", "Accept all")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
